import React, { useState } from 'react';
import { FaUser, FaSync, FaPlus, FaEye, FaEyeSlash, FaChartBar, FaHistory, FaFilter, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import EntryForm from './EntryForm';
import EntryList from './EntryList';
import MetricCard from './MetricCard';
import CategoryChip from './CategoryChip';
import ActionButton from './ActionButton';
import SectionHeader from './SectionHeader';
import UserPermissions from './UserPermissions';

const UserDashboard = ({ user, entries, fetchEntries, darkMode, loading }) => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = Array.from(new Set(entries.map(entry => entry.category)));

  const filteredEntries = entries.filter(entry => {
    const matchCategory = selectedCategory === 'All' || entry.category === selectedCategory;
    let matchDate = true;
    if (startDate && entry.created_at < startDate) matchDate = false;
    if (endDate && entry.created_at > endDate) matchDate = false;
    const matchSearch = !searchTerm || entry.description?.toLowerCase().includes(searchTerm.toLowerCase()) || entry.category?.toLowerCase().includes(searchTerm.toLowerCase()) || entry.amount?.toString().includes(searchTerm);
    return matchCategory && matchDate && matchSearch;
  });

  const totalIncoming = filteredEntries.reduce((sum, entry) => entry.type === 'incoming' ? sum + parseFloat(entry.amount || 0) : sum, 0);
  const totalOutgoing = filteredEntries.reduce((sum, entry) => entry.type === 'outgoing' ? sum + parseFloat(entry.amount || 0) : sum, 0);
  const netBalance = totalIncoming - totalOutgoing;

  const categorySummary = {};
  filteredEntries.forEach(entry => {
    if (!categorySummary[entry.category]) {
      categorySummary[entry.category] = { incoming: 0, outgoing: 0 };
    }
    if (entry.type === 'incoming') {
      categorySummary[entry.category].incoming += parseFloat(entry.amount || 0);
    } else if (entry.type === 'outgoing') {
      categorySummary[entry.category].outgoing += parseFloat(entry.amount || 0);
    }
  });

  const getCategoryBgColor = category => {
    const colors = [
      'bg-blue-100 text-blue-700',
      'bg-green-100 text-green-700',
      'bg-purple-100 text-purple-700',
      'bg-orange-100 text-orange-700',
      'bg-pink-100 text-pink-700',
      'bg-indigo-100 text-indigo-700',
      'bg-teal-100 text-teal-700',
      'bg-red-100 text-red-700'
    ];
    const index = category.length % colors.length;
    return colors[index];
  };

  const clearFilters = () => {
    setSelectedCategory('All');
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedCategory !== 'All' || startDate || endDate || searchTerm;

  const handleDeleteEntry = async entryId => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        const response = await fetch('/api/entries', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: entryId }),
          credentials: 'include'
        });
        if (response.status === 401) throw new Error('Session expired');
        if (!response.ok) throw new Error('Failed to delete entry');
        fetchEntries();
      } catch (error) {
        if (error.message === 'Session expired') throw error;
      }
    }
  };

  const handleUpdateEntry = async (entryId, updatedData) => {
    try {
      const response = await fetch('/api/entries', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: entryId, ...updatedData }),
        credentials: 'include'
      });
      if (response.status === 401) throw new Error('Session expired');
      if (!response.ok) throw new Error('Failed to update entry');
      fetchEntries();
    } catch (error) {
      if (error.message === 'Session expired') throw error;
    }
  };

  const handleRefresh = () => fetchEntries();

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
        {/* Header Section */}
        <SectionHeader
          icon={FaUser}
          title={`Welcome back, ${user?.username || 'Guest'}!`}
          className='mb-4'
        >
          <div className='flex gap-2'>
            <ActionButton icon={FaSync} onClick={handleRefresh} variant='outline'>Refresh</ActionButton>
            <MetricCard
              icon={FaChartBar}
              title='Total Transactions'
              value={filteredEntries.length}
              bgColor='bg-white dark:bg-gray-800'
              className='ml-2'
            />
          </div>
        </SectionHeader>
        <div className='text-gray-600 dark:text-gray-400 text-lg mb-8'>
          Here&apos;s your financial overview and transaction history
        </div>

        {/* Metrics Row */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <MetricCard
            icon={FaArrowUp}
            title='Total Income'
            value={`Rs.${totalIncoming.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            color='text-green-600'
            bgColor='bg-green-50 dark:bg-green-900/20'
            iconBg='bg-green-200 dark:bg-green-900/40'
            percent={'+100% of total'}
            subtext='All incoming transactions'
          />
          <MetricCard
            icon={FaArrowDown}
            title='Total Expenses'
            value={`Rs.${totalOutgoing.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            color='text-red-600'
            bgColor='bg-red-50 dark:bg-red-900/20'
            iconBg='bg-red-200 dark:bg-red-900/40'
            percent={'-0% of total'}
            subtext='All outgoing transactions'
          />
          <MetricCard
            icon={FaChartBar}
            title='Net Balance'
            value={`Rs.${netBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            color='text-blue-600'
            bgColor='bg-blue-50 dark:bg-blue-900/20'
            iconBg='bg-blue-200 dark:bg-blue-900/40'
            percent={'+100% margin'}
            subtext={netBalance >= 0 ? 'Positive balance' : 'Negative balance'}
            highlight={netBalance >= 0}
          />
        </div>

        {/* User Permissions */}
        <div className='mb-8'>
          <UserPermissions darkMode={darkMode} user={user} />
        </div>

        {/* Filters and Search */}
        <SectionHeader icon={FaFilter} title='Search & Filters' className='mb-2'>
          <ActionButton icon={FaFilter} onClick={() => setShowFilters(!showFilters)} variant={showFilters ? 'primary' : 'outline'}>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </ActionButton>
        </SectionHeader>
        {showFilters && (
          <div className='mb-4 grid grid-cols-1 md:grid-cols-4 gap-2'>
            <div>
              <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>Category</label>
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
              >
                <option value='All'>All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>From Date</label>
              <input
                type='date'
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
              />
            </div>
            <div>
              <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>To Date</label>
              <input
                type='date'
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
              />
            </div>
            <div>
              <label className='block mb-1 text-xs font-semibold text-gray-700 dark:text-gray-300'>Search</label>
              <input
                type='text'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='w-full p-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                placeholder='Search by description, category, or amount'
              />
            </div>
            {hasActiveFilters && (
              <div className='col-span-4 flex justify-end'>
                <ActionButton onClick={clearFilters} variant='outline' className='px-3 py-1.5 text-sm'>Clear All Filters</ActionButton>
              </div>
            )}
          </div>
        )}

        {/* Category Summary */}
        <SectionHeader icon={FaChartBar} title='Cash in Hand by Category' />
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8'>
          {Object.entries(categorySummary).map(([category, totals]) => {
            const net = totals.incoming - totals.outgoing;
            return (
              <MetricCard
                key={category}
                icon={FaChartBar}
                title={<CategoryChip label={category} color={getCategoryBgColor(category)} />}
                value={<span className='text-blue-600'>Rs.{net.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                subtext={<>
                  <span className='block text-green-600'>Income: Rs.{totals.incoming.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <span className='block text-red-600'>Expenses: Rs.{totals.outgoing.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </>}
                bgColor='bg-white dark:bg-gray-800'
              />
            );
          })}
        </div>

        {/* Add New Transaction */}
        <SectionHeader icon={FaPlus} title='Add New Transaction'>
          <ActionButton icon={isFormVisible ? FaEyeSlash : FaEye} onClick={() => setIsFormVisible(!isFormVisible)} variant='primary'>
            {isFormVisible ? 'Hide Form' : 'Show Form'}
          </ActionButton>
        </SectionHeader>
        {isFormVisible && (
          <div className='mb-8'>
            <EntryForm fetchEntries={fetchEntries} darkMode={darkMode} />
          </div>
        )}

        {/* Transaction History */}
        <SectionHeader icon={FaHistory} title='Transaction History' />
        <EntryList
          entries={filteredEntries}
          deleteEntry={handleDeleteEntry}
          updateEntry={handleUpdateEntry}
          isAdmin={user?.role === 'admin'}
          darkMode={darkMode}
        />
      </div>
    </div>
  );
};

export default UserDashboard;
